import { serverErrorCode, serverErrorDetail } from '@tallo/design-system';

import { CompanyType } from '../dtos/investor-onboarding-sign-up.dto';
import { InvestorOnboardingStep } from '../enums/investor-onboarding-step.enum';

/** The code the server refuses completion with while a step it requires is not done */
export const stepsMissingErrorCode = 'STEPS_MISSING';

/** Display order of the wizard; the server stores only which steps are done */
const stepOrder: readonly InvestorOnboardingStep[] = [
  InvestorOnboardingStep.AccountType,
  InvestorOnboardingStep.CompanyInfo,
  InvestorOnboardingStep.InviteTeam,
  InvestorOnboardingStep.RentalStrategy,
  InvestorOnboardingStep.ConnectCalendar,
  InvestorOnboardingStep.Communication,
];

/** What someone who joined through an invite answers: only about themselves, since the company is set up */
const personalSteps: readonly InvestorOnboardingStep[] = [
  InvestorOnboardingStep.ConnectCalendar,
  InvestorOnboardingStep.Communication,
];

/**
 * The steps the wizard shows for this account: someone who joined through an invite sees only the
 * personal ones, and individual owners never see the company screen. Inviting the team is the
 * founder's, whatever the account type.
 */
export function visibleOnboardingSteps(
  accountType: CompanyType | null,
  joinedExistingCompany = false,
): InvestorOnboardingStep[] {
  if (joinedExistingCompany) return [...personalSteps];

  return stepOrder.filter((step) => step !== InvestorOnboardingStep.CompanyInfo || accountType !== 'Individual');
}

/**
 * Where the wizard resumes: the first visible step not yet done, or null once every visible step
 * is. A step added later simply shows up as not done, wherever it sits in the order.
 */
export function nextOnboardingStep(
  completedSteps: InvestorOnboardingStep[],
  accountType: CompanyType | null,
  joinedExistingCompany = false,
): InvestorOnboardingStep | null {
  return (
    visibleOnboardingSteps(accountType, joinedExistingCompany).find((step) => !completedSteps.includes(step)) ?? null
  );
}

/** Whether the user may open this step: anything already done, plus the one to do next */
export function isOnboardingStepReachable(
  step: InvestorOnboardingStep,
  completedSteps: InvestorOnboardingStep[],
  accountType: CompanyType | null,
  joinedExistingCompany = false,
): boolean {
  return (
    completedSteps.includes(step) || step === nextOnboardingStep(completedSteps, accountType, joinedExistingCompany)
  );
}

export type OnboardingResume =
  | { kind: 'welcome' }
  | { kind: 'step'; step: InvestorOnboardingStep }
  | { kind: 'finish' };

/**
 * Where a returning user lands. The server's completion flag wins outright, so a done account
 * sees the celebration even if its step list could not be loaded. Otherwise: nothing saved yet
 * opens the welcome screen; something left opens that step; everything saved but the wizard
 * never marked complete (the completion call failed) opens the last step, whose Continue runs
 * it again.
 */
export function resumeOnboarding(
  completedSteps: InvestorOnboardingStep[],
  accountType: CompanyType | null,
  initialOnboardingCompleted: boolean,
  joinedExistingCompany = false,
): OnboardingResume {
  if (initialOnboardingCompleted) return { kind: 'finish' };
  if (!completedSteps.length) return { kind: 'welcome' };

  const next = nextOnboardingStep(completedSteps, accountType, joinedExistingCompany);
  if (next) return { kind: 'step', step: next };

  const visible = visibleOnboardingSteps(accountType, joinedExistingCompany);
  return { kind: 'step', step: visible[visible.length - 1] };
}

/**
 * The step to send the user back to when completing the wizard was refused with STEPS_MISSING:
 * the first one, in wizard order, that the server says is not done. Null for any other failure.
 */
export function firstMissingOnboardingStep(error: unknown): InvestorOnboardingStep | null {
  if (serverErrorCode(error) !== stepsMissingErrorCode) return null;

  const missingSteps = serverErrorDetail(error, 'missingSteps');
  if (!Array.isArray(missingSteps)) return null;

  return stepOrder.find((step) => missingSteps.includes(step)) ?? null;
}
